import { useEffect, useState } from "react";
import { loadGlobalStats, rowFor } from "../services/marketRates";
import { SEGMENTS } from "../services/constants";
import { formatRatePer1k, formatFlatPrice, relativeTime } from "../utils/format";
import "./RateTicker.css";

// Compact market-rate strip mounted above the Directory filters. Reads
// the nightly aggregate from pop_market_meta/global_stats. Silent when
// the sample is too small to be meaningful.
//
// Props:
//   game     — "BGMI" | "PUBGM" | "" (defaults to BGMI when unfiltered)
//   currency — ISO currency the buyer is browsing in. INR only for now.
const MIN_SAMPLE = 3;

export default function RateTicker({ game, currency = "INR" }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadGlobalStats().then((s) => {
      if (!cancelled) { setStats(s); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  const effectiveGame = game || "BGMI";
  const row = stats ? rowFor(stats, effectiveGame, currency) : null;
  if (loading || !row) return null;

  const cells = SEGMENTS
      .map((seg) => ({ seg, bucket: row[seg.key] }))
      .filter(({ bucket }) => bucket && bucket.sampleSize >= MIN_SAMPLE);
  if (cells.length === 0) return null;

  const updated = stats.lastUpdatedAt
      ? relativeTime({ toDate: () => new Date(stats.lastUpdatedAt) })
      : null;

  return (
    <div className="rate-ticker" role="region" aria-label="Market rates today">
      <span className="rt-label">
        Today · {effectiveGame} · {currency}
      </span>
      <div className="rt-cells">
        {cells.map(({ seg, bucket }) => {
          const isFlat = seg.key === "lumen" || seg.key === "helicopter";
          const fmt = isFlat ? formatFlatPrice : formatRatePer1k;
          return (
            <span key={seg.key}
              className={`rt-cell ${seg.cssClass}`}
              title={`p25 ${fmt(bucket.p25, currency)} · p75 ${fmt(bucket.p75, currency)} · ${bucket.sampleSize} sellers`}>
              <span className="rt-seg">{seg.label}</span>
              <span className="rt-value">{fmt(bucket.median, currency)}</span>
            </span>
          );
        })}
      </div>
      {updated && <span className="rt-updated" title="Aggregated nightly from active sellers">Updated {updated}</span>}
    </div>
  );
}
